import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { renderWithMentions } from './mentions';
import DoneIteratingButton from './DoneIteratingButton';
import { GuideLink } from '../common';
import { ROLE_GUIDES } from '../../utils';
import CancelCycleButton from './CancelCycleButton';
import ReviewSequenceIndicator from './ReviewSequenceIndicator';
import OpenStepPanel from './OpenStepPanel';
import MissingStepWarning from './MissingStepWarning';
import ConfirmFixesPanel from './ConfirmFixesPanel';
import MarkStepCompleteButton from './MarkStepCompleteButton';
import RevisionBanner from './RevisionBanner';
import ChangeSpecPanel from './ChangeSpecPanel';
import UnlinkLineageButton from './UnlinkLineageButton';
import RenameCycleButton from './RenameCycleButton';
import DocumentViewer from './DocumentViewer';
import DeadlinePill from './DeadlinePill';
import DeadlineEditor from './DeadlineEditor';
import AssignedDmEditor from './AssignedDmEditor';
import CommentEditForm from './CommentEditForm';
import type { User, CycleWithComments, ReviewComment, ReviewCycle, CycleStatus, Disposition } from './types';
import { DISPOSITION_LABELS, isUndecided, carriesDisposition } from './types';
import { automationSummary, AUTOMATION_STEP_LABELS } from './constants';
import './ContentReview.css';

interface DecisionViewProps {
  user: User | null;
  cycleId: string;
  getAuthHeaders: () => Promise<Record<string, string>>;
  // Takes an optional flash string (ReviewInterface passes one after a
  // decision). Typed honestly so `onClick={onBack}` is a COMPILE error rather
  // than a runtime one: bare, React hands the click event to `flash`, the
  // dashboard renders it as a child, and React #31 unmounts the whole app —
  // a white page, which is exactly what happened in the 2026-08-21 prod E2E.
  onBack: (flash?: string) => void;
  // Phase 8E — caller passes whether the user can cancel cycles. Optional
  // for back-compat: defaults to false so non-passing callers (e.g. legacy
  // tests) don't see the cancel button.
  canCancelCycles?: boolean;
  // Delete a CHILD cycle from its tab. Same permission as the dashboard's
  // Delete (canMakeDecisions) and the backend's requireDecisionMaker.
  canDeleteChildCycles?: boolean;
  // Phase 8C remainder — caller passes whether the user can unlink lineage
  // (manage_submissions or admin role; DM does not inherit per D3).
  canUnlinkLineage?: boolean;
  // currentUserStep resolves to 'medical'/'legal'/'producer' or null for
  // viewer-only users (they can't acquire).
  currentUserStep?: 'medical' | 'legal' | 'producer' | null;
  // O-2 — mirrors the backend requireDecisionMaker gate on
  // POST /cycles/:id/pipeline/rerun.
  canRerun?: boolean;
  // Editing someone else's comment. The viewer renders read-only here, which
  // is NOT the same question — see DocumentViewer.canEditAnyComment.
  canEditAnyComment?: boolean;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

const STATUS_LABELS: Record<CycleStatus, string> = {
  pending_prescreen: 'Awaiting Pre-Screen',
  pending_review: 'Pending Review',
  in_review: 'In Review',
  approved: 'Approved',
  revision_needed: 'Revision Needed',
  rejected: 'Rejected',
  // Phase 8E — admin canceled the cycle (e.g. producer abandoned, content
  // scrapped). Surfaces in the UI so reviewers don't try to act on it.
  canceled: 'Canceled',
  superseded: 'Superseded'
};

const DecisionView: React.FC<DecisionViewProps> = ({
  user, cycleId, getAuthHeaders, onBack,
  canCancelCycles = false, canUnlinkLineage = false, canDeleteChildCycles = false,
  currentUserStep = null,
  canRerun = false,
  canEditAnyComment = false
}) => {
  const [data, setData] = useState<CycleWithComments | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [decisionNotes, setDecisionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // WS1.3 — post-decision confirmation banner (walk finding #7: silent success).
  const [decisionConfirmation, setDecisionConfirmation] = useState<string | null>(null);
  const [childComments, setChildComments] = useState<ReviewComment[]>([]);
  const [showEditReviewers, setShowEditReviewers] = useState(false);
  const [teamMembers, setTeamMembers] = useState<{ _id: string; name: string; email: string }[]>([]);
  const [selectedReviewerIds, setSelectedReviewerIds] = useState<string[]>([]);
  const [savingReviewers, setSavingReviewers] = useState(false);
  // O-2 — pipeline re-run (backgrounded server-side; we poll for the result).
  const [rerunning, setRerunning] = useState(false);
  const [rerunNotice, setRerunNotice] = useState<string | null>(null);

  // Only blank the screen on initial load, not on refreshes — otherwise
  // DocumentViewer unmounts after every comment submit and loses its state.
  const hasLoadedOnceRef = useRef(false);

  // Which viewer tab is open. DecisionView had no tab state at all until
  // 2026-08-24: DocumentViewer owned it internally, so the Final Decision
  // panel below sat there unchanged while the DM was looking at an embedded
  // item, reading as "decide here too" when there has only ever been ONE
  // decision covering the whole review (Fredrik, walking the decision view).
  const [activeTab, setActiveTab] = useState<string>('parent');
  // Full child cycles keyed by id, so the per-item panel can read reviewedBy.
  const [childCycles, setChildCycles] = useState<Record<string, ReviewCycle>>({});
  const [markingReviewed, setMarkingReviewed] = useState(false);

  // Bumped on every cycle refetch so DocumentViewer re-reads its children.
  // Without it, deleting an embedded child leaves its tab in the strip:
  // onChildDeleted refetches the cycle, but the children effect is keyed on
  // [cycle._id, cycle.contentType], neither of which changes (2026-08-21 audit).
  const [dataVersion, setDataVersion] = useState(0);

  const fetchCycle = useCallback(async () => {
    try {
      if (!hasLoadedOnceRef.current) setLoading(true);
      const headers = await getAuthHeaders();
      const res = await axios.get(`${backendUrl}/api/content-review/cycles/${cycleId}`, { headers });
      setData(res.data);
      if (res.data.cycle.decisionMakerNotes) {
        setDecisionNotes(res.data.cycle.decisionMakerNotes);
      }

      // Fetch child review comments
      try {
        const parsedRes = await axios.get(`${backendUrl}/api/content-review/cycles/${cycleId}/parsed-content`, { headers });
        const children = parsedRes.data.children || [];
        if (children.length > 0) {
          const allChildComments: ReviewComment[] = [];
          const cycleMap: Record<string, ReviewCycle> = {};
          for (const child of children) {
            const childRes = await axios.get(`${backendUrl}/api/content-review/cycles/${child._id}`, { headers });
            cycleMap[child._id] = childRes.data.cycle;
            allChildComments.push(...(childRes.data.comments || []).map((c: any) => ({
              ...c,
              _childName: child.contentItem.name
            })));
          }
          setChildComments(allChildComments);
          setChildCycles(cycleMap);
        } else {
          setChildCycles({});
        }
      } catch (childErr) {
        console.error('Error fetching child comments:', childErr);
      }

      setDataVersion(v => v + 1);
      setError(null);
      hasLoadedOnceRef.current = true;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch review cycle');
    } finally {
      setLoading(false);
    }
  }, [cycleId, getAuthHeaders]);

  useEffect(() => { fetchCycle(); }, [fetchCycle]);

  // O-2 — kick the backgrounded pipeline re-run, then poll the cycle until
  // automationRerun.completedAt lands. Backgrounded server-side because the
  // pipeline polls HubSpot/Graph and can outrun Render's 30s gateway.
  const handleRerunPipeline = async () => {
    setRerunning(true);
    setRerunNotice(null);
    try {
      const headers = await getAuthHeaders();
      await axios.post(
        `${backendUrl}/api/content-review/cycles/${cycleId}/pipeline/rerun`,
        {}, { headers }
      );
      setRerunNotice('Re-run started — this can take up to a minute.');

      // Poll for completion (max ~2 min), then refresh the view.
      const deadline = Date.now() + 120000;
      let done = false;
      while (Date.now() < deadline && !done) {
        await new Promise(r => setTimeout(r, 3000));
        const poll = await axios.get(
          `${backendUrl}/api/content-review/cycles/${cycleId}`,
          { headers: await getAuthHeaders() }
        );
        const rr = poll.data?.cycle?.automationRerun;
        if (rr?.completedAt) {
          done = true;
          setRerunNotice(rr.error
            ? `Could not finish the re-run: ${rr.error}`
            : 'Re-run finished — statuses below are up to date.');
        }
      }
      if (!done) {
        setRerunNotice('Still running — reopen this cycle shortly to see the result.');
      }
      await fetchCycle();
    } catch (err: any) {
      setRerunNotice(`Could not start the re-run: ${err.response?.data?.error || 'request failed'}`);
    } finally {
      setRerunning(false);
    }
  };

  // Per-item "I've reviewed this" for the decision maker (2026-08-24). Same
  // endpoint and same array the reviewers and the producer use — entries are
  // per-person, so this records the DM's own look and stands in for nobody
  // else's. Approval is refused server-side while any item is unconfirmed.
  const handleToggleChildReviewed = async (childCycleId: string, makeReviewed: boolean) => {
    try {
      setMarkingReviewed(true);
      const headers = await getAuthHeaders();
      if (makeReviewed) {
        await axios.post(`${backendUrl}/api/content-review/cycles/${childCycleId}/reviewed`, {}, { headers });
      } else {
        await axios.delete(`${backendUrl}/api/content-review/cycles/${childCycleId}/reviewed`, { headers });
      }
      await fetchCycle();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update reviewed status');
    } finally {
      setMarkingReviewed(false);
    }
  };

  const handleDecision = async (finalDecision: 'approved' | 'revised' | 'rejected') => {
    const statusMap: Record<string, CycleStatus> = {
      approved: 'approved',
      revised: 'revision_needed',
      rejected: 'rejected'
    };

    try {
      setSubmitting(true);
      const headers = await getAuthHeaders();
      await axios.put(`${backendUrl}/api/content-review/cycles/${cycleId}/status`, {
        status: statusMap[finalDecision],
        finalDecision,
        decisionMakerNotes: decisionNotes.trim()
      }, { headers });
      // WS1.3 (walk finding #7) — the decision used to silently succeed;
      // the DM had no confirmation anything happened. Show an explicit
      // banner, then return to the dashboard after a beat so the flow
      // has a visible end.
      const label = finalDecision === 'approved'
        ? 'Approved'
        : finalDecision === 'rejected'
          ? 'Rejected'
          : 'Revision requested';
      setDecisionConfirmation(`${label} — recorded ${new Date().toLocaleTimeString()}. Returning to dashboard…`);
      await fetchCycle();
      setTimeout(() => { onBack(); }, 2500);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit decision');
      // The banner at the top is out of the viewport when the action bar is
      // at the bottom of a long cycle — surface it beside the buttons too.
      setTimeout(() => document.getElementById('decision-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleResolved = async (comment: ReviewComment) => {
    try {
      const headers = await getAuthHeaders();
      await axios.put(`${backendUrl}/api/content-review/comments/${comment._id}`, {
        resolved: !comment.resolved
      }, { headers });
      fetchCycle();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update comment');
    }
  };

  // Spec §2.2 / Phase 4 — disposition is the DM's decision on a finding.
  // "Won't apply" asks for the reason inline; the server refuses without one.
  // "Won't apply" collects its reason in a centred modal (window.prompt
  // rendered at the top of the viewport and could not be styled).
  const [reasonTarget, setReasonTarget] = useState<ReviewComment | null>(null);
  const [reasonText, setReasonText] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);

  const submitDisposition = async (comment: ReviewComment, disposition: Disposition, reason = '') => {
    const headers = await getAuthHeaders();
    await axios.put(`${backendUrl}/api/content-review/comments/${comment._id}/disposition`, { disposition, reason }, { headers });
    fetchCycle();
  };

  const handleSetDisposition = async (comment: ReviewComment, disposition: Disposition) => {
    if (disposition === 'wont_apply') {
      setReasonTarget(comment);
      setReasonText(comment.dispositionReason || '');
      setReasonError(null);
      return;
    }
    try {
      await submitDisposition(comment, disposition);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to set disposition');
    }
  };

  const saveWontApplyReason = async () => {
    if (!reasonTarget) return;
    const reason = reasonText.trim();
    if (!reason) { setReasonError('A reason is required — it is the record of why this finding did not reach the asset.'); return; }
    try {
      await submitDisposition(reasonTarget, 'wont_apply', reason);
      setReasonTarget(null);
    } catch (err: any) {
      setReasonError(err.response?.data?.error || 'Failed to set disposition');
    }
  };


  const handleApplyRemaining = async () => {
    try {
      const headers = await getAuthHeaders();
      const r = await axios.post(`${backendUrl}/api/content-review/cycles/${cycleId}/dispositions/apply-remaining`, {}, { headers });
      setDecisionConfirmation(`Marked ${r.data?.applied ?? 0} finding(s) as "Apply".`);
      fetchCycle();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to apply remaining');
    }
  };

  const openEditReviewers = async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await axios.get(`${backendUrl}/api/team`, { headers });
      const eligible = (res.data || []).filter((m: any) =>
        m.permissions?.subcategories?.includes('content_review.reviewer') ||
        m.role === 'admin'
      );
      setTeamMembers(eligible);
      setSelectedReviewerIds(data?.cycle.reviewers.map(r => r._id) || []);
      setShowEditReviewers(true);
    } catch (err) {
      console.error('Error fetching team:', err);
    }
  };

  const saveReviewers = async () => {
    try {
      setSavingReviewers(true);
      const headers = await getAuthHeaders();
      await axios.put(`${backendUrl}/api/content-review/cycles/${cycleId}/reviewers`, {
        reviewerIds: selectedReviewerIds
      }, { headers });
      setShowEditReviewers(false);
      fetchCycle();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update reviewers');
    } finally {
      setSavingReviewers(false);
    }
  };

  const toggleReviewer = (id: string) => {
    setSelectedReviewerIds(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  if (loading) return <div className="content-review" style={{ textAlign: 'center', padding: 40 }}>Loading...</div>;
  if (!data) return <div className="content-review"><p>Review cycle not found.</p></div>;

  const { cycle, comments } = data;
  // O-7 — current automation state is latest-entry-per-step, not the raw
  // append-only trail (see automationSummary).
  const automationView = automationSummary(cycle.automation as any);
  const canRerunPipeline = canRerun &&
    (cycle.finalDecision === 'approved' || cycle.status === 'approved');
  // Fredrik 2026-09-02 — a decided cycle must not offer a re-decision
  // (side effects: pipeline, revision emails; reopening goes through the
  // new-version/supersede flow). pendingFixes keeps its section: the DM's
  // remaining act (confirm fixes) lives there — the 2026-08-31 fix.
  const cycleIsTerminal = ['approved', 'rejected', 'canceled', 'superseded'].includes(cycle.status);
  // Spec §8 — consultation threads live ONLY in the Consultations panel
  // (replies inherit the root's kind server-side, so this drops whole threads).
  const allComments = [...comments, ...childComments].filter(c => c.kind !== 'consultation');

  // Group comments by reviewer
  const commentsByReviewer: Record<string, ReviewComment[]> = {};
  allComments.forEach(c => {
    const key = c.reviewer.email;
    if (!commentsByReviewer[key]) commentsByReviewer[key] = [];
    commentsByReviewer[key].push(c);
  });

  // Summary stats
  const unresolvedCount = allComments.filter(c => !c.resolved).length;
  const criticalCount = allComments.filter(c => c.severity === 'critical' && !c.resolved).length;
  // Spec §2.2 — findings still awaiting the DM's decision; critical/major
  // ones block approval (server-enforced, DISPOSITIONS_UNDECIDED).
  const findingRoots = allComments.filter(carriesDisposition);
  // Mirrors the backend's STEPS_NOT_COMPLETE gate so Approve is disabled rather
  // than refused after the click. Derived from the same two fields the server
  // compares — an empty sequence means no gate, exactly as there.
  const stepsOpen = (() => {
    const seq = [...(cycle?.reviewSequence || [])].sort((a, b) => a.order - b.order);
    if (seq.length === 0) return [] as string[];
    const done = new Set((cycle?.stepCompletions || []).map(sc => sc.step));
    return seq.filter(st => !done.has(st.step)).map(st => st.step);
  })();
  const undecidedFindings = findingRoots.filter(isUndecided);
  const undecidedBlocking = undecidedFindings.filter(c => c.severity === 'critical' || c.severity === 'major').length;
  const assessmentsSubmitted = cycle.reviewerAssessments.filter(a => a.submittedAt != null).length;
  const totalReviewers = cycle.reviewerAssessments.length;

  return (
    <div className="content-review">
      <button className="review-back-btn" onClick={() => onBack()}>&#8592; Back to Dashboard</button>

      <h2>{cycle.contentItem.name} <GuideLink href={ROLE_GUIDES.decisionMaker} label="Decision maker guide" /></h2>
      <RevisionBanner cycle={cycle} getAuthHeaders={getAuthHeaders} />

      {/* Producer's upload notes — carried past prescreen (Fredrik, 2026-09-02). */}
      {cycle.submissionNotes && (
        <div style={{ background: '#f0f7ff', padding: 14, borderRadius: 8, borderLeft: '3px solid #123250', marginBottom: 16, fontSize: 14, color: '#333' }}>
          <strong style={{ fontSize: 12, color: '#123250', display: 'block', marginBottom: 4 }}>Submission Notes</strong>
          {cycle.submissionNotes}
        </div>
      )}
      {/* Fix pass 2026-08-10 (Finding 2) — surface producer.name. */}
      {cycle.producer?.name && (
        <div style={{ fontSize: 13, color: '#6c757d', marginTop: -8, marginBottom: 12 }}>
          Producer: <span style={{ color: '#495057', fontWeight: 500 }}>{cycle.producer.name}</span>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
        <span className={`review-status-badge ${cycle.status}`}>
          {cycle.pendingFixes && cycle.status === 'approved'
            ? 'Approved — awaiting fixes'
            : STATUS_LABELS[cycle.status]}
        </span>
        {cycle.contentItem.type && (
          <span className="review-meta-tag purpose">
            <span style={{ fontWeight: 400, marginRight: 4 }}>Type</span>{cycle.contentItem.type}
          </span>
        )}
        {cycle.contentItem.audience && (
          <span className="review-meta-tag audience">
            <span style={{ fontWeight: 400, marginRight: 4 }}>Audience</span>{cycle.contentItem.audience}
          </span>
        )}
        {/* v2 (D90) — topic + geography chips, aligned with the dashboard
            cards (raw slugs here; the dashboard resolves labels). Geo
            hidden for global — the default carries no information. */}
        {(cycle.contentItem as any).topic && (
          <span className="review-meta-tag" style={{ background: '#e7f5ec', color: '#1e6b3a' }}>
            <span style={{ fontWeight: 400, marginRight: 4 }}>Topic</span>{(cycle.contentItem as any).topic}
          </span>
        )}
        {(cycle.contentItem as any).geography && (cycle.contentItem as any).geography !== 'global' && (
          <span className="review-meta-tag" style={{ background: '#fdf1e0', color: '#8a5a00' }}>
            <span style={{ fontWeight: 400, marginRight: 4 }}>Geo</span>{(cycle.contentItem as any).geography}
          </span>
        )}
        {cycle.contentItem.version && (
          <span className="review-meta-tag version" title="Draft version (X.Y — approvals stamp V<major>.0 in the archive)">
            V{cycle.contentItem.version}
          </span>
        )}
        {/* 14e walk station 4 — editable source visible in-cycle, aligned
            with PreScreenView + dashboard cards. Read-only display. */}
        {(cycle.contentItem as any).sourceLink && (
          // vm: links have no navigable target — render a span, not an
          // href-less anchor (jsx-a11y/anchor-is-valid; Vercel cold builds
          // treat lint warnings as errors under CI=true).
          (cycle.contentItem as any).sourceLink.startsWith('vm:') ? (
            <span
              className="review-meta-tag"
              style={{ background: '#e8f0fe', color: '#1a58c2', fontWeight: 500 }}
              title={`Source (VM project — open on the render VM): ${(cycle.contentItem as any).sourceLink}`}
            >
              🔗 Source
            </span>
          ) : (
            <a
              className="review-meta-tag"
              href={(cycle.contentItem as any).sourceLink}
              target="_blank" rel="noopener noreferrer"
              style={{ background: '#e8f0fe', color: '#1a58c2', fontWeight: 500, textDecoration: 'none' }}
              title={`Editable source: ${(cycle.contentItem as any).sourceLink}`}
            >
              🔗 Source
            </a>
          )
        )}
        {(cycle.contentItem as any).sourceFileId && (
          <span className="review-meta-tag" style={{ background: '#e6f4ea', color: '#1e6f2c', fontWeight: 500 }}
            title={`Editable companion: ${(cycle.contentItem as any).sourceFileName || 'source file'}`}>
            📎 Source file
          </span>
        )}
        {(cycle.contentItem as any).noEditableSourceReason && (
          <span className="review-meta-tag" style={{ background: '#fff4e5', color: '#a15c00', fontWeight: 500 }}
            title={`Source waived — reason: ${(cycle.contentItem as any).noEditableSourceReason}`}>
            ⚠ No source ({(cycle.contentItem as any).noEditableSourceReason})
          </span>
        )}
        <DeadlinePill deadline={cycle.reviewDeadline} />
        {cycle.finalDecision && (
          <span style={{ fontSize: 14, fontWeight: 600, color: '#123250' }}>
            Decision: {cycle.finalDecision}
            {/* WS1.2 — decision audit surfaced (walk finding #8). */}
            {cycle.decidedBy?.name && (
              <span style={{ fontWeight: 400, color: '#6c757d' }}>
                {' '}by {cycle.decidedBy.name}
                {cycle.decidedAt && ` · ${new Date(cycle.decidedAt).toLocaleString()}`}
              </span>
            )}
          </span>
        )}
        {/* WS1.4 — sequence position strip, same shape as ReviewInterface
            (was missed in the Batch B port; DMs had to toggle to Reviewer
            View to see it — walk finding, 2026-08-10). */}
        <ReviewSequenceIndicator cycle={cycle} currentUserStep={currentUserStep} currentUserEmail={user?.email || null} />
        <MissingStepWarning cycle={cycle} getAuthHeaders={getAuthHeaders} />
        <DoneIteratingButton cycle={cycle} getAuthHeaders={getAuthHeaders} onComplete={fetchCycle} />
        {/* Phase 12E — "Mark my step complete" (or "Mark ready to distribute"
            when the user is the producer at step 3). Only renders when the
            user's step is next in the reviewSequence. */}
        <MarkStepCompleteButton
          cycle={cycle}
          currentUserStep={currentUserStep}
          getAuthHeaders={getAuthHeaders}
          onComplete={fetchCycle}
        />
        {/* Phase 8E — admin "Cancel cycle". Renders only for admin/DM and
            only on non-terminal cycles. Used to unblock a stuck lineage. */}
        <CancelCycleButton cycle={cycle} canCancel={canCancelCycles} getAuthHeaders={getAuthHeaders} onComplete={fetchCycle} />
        {/* Phase 8C remainder — admin "Fork lineage" (rename + unlink in
            one go) and a standalone "Rename" for metadata typos. Both
            gated on manage_submissions (admin role inherits). */}
        <UnlinkLineageButton cycle={cycle} canUnlink={canUnlinkLineage} getAuthHeaders={getAuthHeaders} onComplete={fetchCycle} />
        <RenameCycleButton cycle={cycle} canRename={canUnlinkLineage} getAuthHeaders={getAuthHeaders} onComplete={fetchCycle} />
      </div>

      {error && <div style={{ color: '#dc3545', padding: 12, background: '#f8d7da', borderRadius: 8, marginBottom: 16 }}>{error}</div>}
      {/* WS1.3 — decision confirmation banner. */}
      {decisionConfirmation && (
        <div
          role="status"
          style={{ color: '#155724', padding: 12, background: '#d4edda', borderRadius: 8, marginBottom: 16, fontWeight: 600 }}
          data-testid="decision-confirmation"
        >
          ✓ {decisionConfirmation}
        </div>
      )}
      {/* WS3 — post-approval automation trail. Only renders once the
          pipeline has run; failed steps get the red treatment so a broken
          SharePoint copy is impossible to miss.
          O-2/O-7 (2026-08-14): reads LATEST-per-step (the pipeline appends
          on every run, so the raw array double-counts after a re-run) and
          offers the re-run to decision makers. */}
      {automationView.ran && (
        <div
          data-testid="automation-panel"
          style={{
            padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13,
            background: automationView.hasFailure ? '#f8d7da' : '#f4f6fa',
            border: '1px solid #dee2e6'
          }}
        >
          <strong style={{ display: 'block', marginBottom: 6, color: '#123250' }}>
            Post-approval automation
            {automationView.hasFailure && (
              <span style={{ color: '#dc3545' }}> — needs attention</span>
            )}
          </strong>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {automationView.steps.map((a, i) => (
              <li key={i} style={{ color: a.status === 'failed' ? '#dc3545' : a.status === 'skipped' ? '#856404' : '#155724' }}>
                {a.status === 'ok' ? '✓' : a.status === 'skipped' ? '↷' : '✗'}{' '}
                <strong>{AUTOMATION_STEP_LABELS[a.step] || a.step}</strong>
                {a.detail && <span style={{ color: '#555' }}> — {a.detail}</span>}
                {a.error && <span> — {a.error}</span>}
                {a.at && <span style={{ color: '#999', fontSize: 11 }}> · {new Date(a.at).toLocaleTimeString()}</span>}
              </li>
            ))}
          </ul>
          {rerunNotice && (
            <div style={{ marginTop: 8, fontSize: 12, color: rerunNotice.startsWith('Could not') ? '#a6231a' : '#155724' }}>
              {rerunNotice}
            </div>
          )}
          {canRerunPipeline && (
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                data-testid="pipeline-rerun-btn"
                onClick={handleRerunPipeline}
                disabled={rerunning}
                style={{
                  padding: '6px 12px', fontSize: 12, borderRadius: 6, cursor: rerunning ? 'default' : 'pointer',
                  border: '1px solid #123250',
                  background: rerunning ? '#e9ecef' : '#fff',
                  color: rerunning ? '#6c757d' : '#123250'
                }}
              >
                {rerunning ? 'Re-running…' : 'Re-run automation'}
              </button>
              <span style={{ marginLeft: 8, fontSize: 11, color: '#6c757d' }}>
                Safe to repeat — steps that already succeeded are re-applied to the same destinations.
              </span>
            </div>
          )}
        </div>
      )}

      {/* AI Pre-Review */}
      {cycle.aiPreReview && (
        <div className="ai-prereview">
          <h4>AI Pre-Review Summary</h4>
          <p>{cycle.aiPreReview}</p>
        </div>
      )}

      {/* Document Viewer (read-only for decision maker) */}
      {cycle.contentType && (
        <div className="review-section">
          <h3>Content</h3>
          <DocumentViewer
            user={user}
            cycle={cycle}
            comments={comments}
            getAuthHeaders={getAuthHeaders}
            onCommentAdded={fetchCycle}
            readOnly
            canEditAnyComment={canEditAnyComment}
            // "All Comments" moved into the rail (2026-08-26). The card is
            // untouched — disposition select, the per-item confirmation gate
            // in handleSetDisposition, resolve — only its container changed.
            // The by-reviewer grouping is gone with the section: every row
            // names its author, which is what the grouping was for.
            renderCommentDetail={(c) => (
              <DecisionCommentCard
                comment={c}
                onToggleResolved={handleToggleResolved}
                onSetDisposition={handleSetDisposition}
                inRail
                canEdit={canEditAnyComment
                  || (c.reviewer?.email || '').toLowerCase() === (user?.email || '').toLowerCase()}
                canDelete={(c.reviewer?.email || '').toLowerCase() === (user?.email || '').toLowerCase()}
                getAuthHeaders={getAuthHeaders}
                onEdited={fetchCycle}
              />
            )}
            commentsHeader={
              <div className="rail-section-head" data-testid="dm-comments-header">
                <span className={unresolvedCount > 0 ? 'rail-count-warn' : 'rail-count-ok'}>
                  {unresolvedCount} unresolved
                </span>
                {criticalCount > 0 && <span className="rail-count-warn">{criticalCount} critical</span>}
                {undecidedFindings.length > 0 && (
                  <span
                    className={undecidedBlocking > 0 ? 'rail-count-warn' : 'rail-count-muted'}
                    title="Findings without a disposition. Critical/major ones block approval."
                  >
                    {undecidedFindings.length} undecided{undecidedBlocking > 0 ? ` (${undecidedBlocking} blocking)` : ''}
                  </span>
                )}
                {undecidedFindings.length > 0 && (
                  <button
                    className="review-btn review-btn-outline rail-btn"
                    onClick={handleApplyRemaining}
                    title="Mark every undecided finding as Apply — an explicit, audited click"
                  >
                    Apply all remaining ({undecidedFindings.length})
                  </button>
                )}
              </div>
            }
            refreshKey={dataVersion}
            activeTab={activeTab}
            onActiveTabChange={setActiveTab}
            canDeleteChild={canDeleteChildCycles}
            onChildDeleted={fetchCycle}
          />
        </div>
      )}

      {/* Phase 12F — @mention audit trail */}
      {/* 2026-08-26 — ConsultationsPanel moved INTO the viewer's right rail
          (ViewerSidePanel). It was here, below the document, so answering a
          question meant scrolling past the whole item and then losing sight
          of the content you were answering about. Rendering it in both places
          would double-fetch and give two sources of truth for one thread. */}

      {/* Risk Matrix Overview */}
      <div className="review-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Risk Assessment Overview ({assessmentsSubmitted}/{totalReviewers} submitted)</h3>
          <button
            className="review-btn review-btn-outline"
            onClick={openEditReviewers}
            style={{ fontSize: 12, padding: '6px 12px' }}
          >
            Edit Reviewers
          </button>
        </div>

        {/* Phase 6B — DM can adjust the deadline alongside reviewer changes */}
        {cycle.status !== 'approved' && cycle.status !== 'rejected' && (
          <div style={{ marginBottom: 16, padding: '12px 14px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e5e7eb' }}>
            <DeadlineEditor
              cycleId={cycleId}
              currentDeadline={cycle.reviewDeadline}
              getAuthHeaders={getAuthHeaders}
              onUpdated={fetchCycle}
              compact
            />
            <AssignedDmEditor
              cycleId={cycleId}
              current={cycle.assignedDecisionMakers || []}
              getAuthHeaders={getAuthHeaders}
              onUpdated={fetchCycle}
              canEdit
            />
          </div>
        )}

        <div className="review-stats">
          {cycle.reviewerAssessments.map((a, i) => {
            const reviewer = a.reviewer;
            return (
              <div key={i} className="review-stat-card" style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: 600, marginBottom: 8, color: '#123250' }}>{reviewer?.name || 'Reviewer'}</div>
                {a.submittedAt ? (
                  <>
                    <div style={{ marginBottom: 6 }}>
                      Risk level: {a.riskLevel ? <span className={`risk-badge ${a.riskLevel}`}>{a.riskLevel}</span> : <span style={{ color: '#999' }}>-</span>}
                    </div>
                    {a.summary && <div style={{ fontSize: 13, color: '#555', fontStyle: 'italic' }}>{a.summary}</div>}
                  </>
                ) : (
                  <div style={{ color: '#6c757d', fontSize: 13 }}>Assessment pending</div>
                )}
              </div>
            );
          })}
        </div>

        {/* The producer's commercial perspective (2026-08-24) — beside the
            reviewer assessments because it is the same kind of thing from the
            DM's side: an input to weigh. Deliberately styled apart from the
            risk cards and carrying no risk badge, because it is NOT a risk
            judgement — the producer is not making one. */}
        {cycle.producerReadback?.submittedAt && (
          <div
            data-testid="producer-readback-card"
            style={{ marginTop: 14, padding: '12px 14px', background: '#fdf6ec', border: '1px solid #f0d9b5', borderRadius: 8 }}
          >
            <div style={{ fontWeight: 600, marginBottom: 6, color: '#123250' }}>
              Producer — commercial perspective
              <span style={{ fontWeight: 400, color: '#8a6d3b', fontSize: 12, marginLeft: 8 }}>
                not a risk assessment
              </span>
            </div>
            <div style={{ fontSize: 13, color: '#333', whiteSpace: 'pre-wrap' }}>
              {cycle.producerReadback.text}
            </div>
            <div style={{ fontSize: 12, color: '#8a6d3b', marginTop: 6 }}>
              {cycle.producerReadback.submittedBy?.name || 'Producer'} &middot;{' '}
              {new Date(cycle.producerReadback.submittedAt).toLocaleString()}
            </div>
          </div>
        )}

        {/* Decision maker notes beside the assessments once a decision exists
            (Fredrik 2026-09-03): they were entered at decision time and then
            shown nowhere — only the archive's rationale and the revision
            email carried them. Same section as the reviewer cards because
            they are the other half of the same record: what the reviewers
            said, and what the DM concluded. While the cycle is still
            decidable the notes live in the Final Decision textarea below —
            this card is the read-back after the fact. */}
        {cycle.finalDecision && cycle.decisionMakerNotes && (
          <div
            data-testid="dm-notes-card"
            style={{ marginTop: 14, padding: '12px 14px', background: '#eef4fb', border: '1px solid #c9dcf0', borderRadius: 8 }}
          >
            <div style={{ fontWeight: 600, marginBottom: 6, color: '#123250' }}>
              Decision maker — notes
            </div>
            <div style={{ fontSize: 13, color: '#333', whiteSpace: 'pre-wrap' }}>
              {cycle.decisionMakerNotes}
            </div>
            <div style={{ fontSize: 12, color: '#4a6a8a', marginTop: 6 }}>
              {cycle.decidedBy?.name || 'Decision maker'}
              {cycle.decidedAt && <> &middot; {new Date(cycle.decidedAt).toLocaleString()}</>}
            </div>
          </div>
        )}
      </div>

      {/* "All Comments" lived here until 2026-08-26. It is the rail's comments
          tab now — the DM reads a finding while looking at the passage it is
          about, and decides its disposition without leaving that view. The
          counters and "Apply all remaining" went with it as the tab's header.
          Two lists would duplicate `id="comment-<id>"`, which is what every
          marker click resolves by. */}

      {/* On an embedded item, the decision panel gives way to that ITEM's
          business: read it, confirm you have, go back to decide. There is one
          decision and it covers the whole review — the panel simply never said
          so, which is what made it look like a second one. */}
      {activeTab !== 'parent' && childCycles[activeTab] && (() => {
        const childCycle = childCycles[activeTab];
        const childName = childCycle.contentItem.name;
        const itemComments = childComments.filter(c => (c as any)._childName === childName);
        const isSeen = (childCycle.reviewedBy || []).some(
          (r: any) => r?.reviewer?.email?.toLowerCase() === (user?.email || '').toLowerCase()
        );
        return (
          <div className="review-section" data-testid="dm-child-panel">
            <h3>Reviewed: {childName}</h3>
            <div className="viewer-tab-hint">
              <strong>The decision is made once, on the main tab</strong>, and covers this item
              too — its findings are already in the consolidated list and its critical and major
              ones block approval there. What is left here is confirming you have seen the item
              itself.
            </div>
            {isSeen ? (
              <div style={{ background: '#d4edda', padding: 14, borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 18, color: '#155724' }}>&#10003;</span>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <strong style={{ color: '#155724' }}>You've reviewed this item.</strong>
                  <p style={{ margin: '2px 0 0', fontSize: 13, color: '#555' }}>
                    {itemComments.length > 0
                      ? `${itemComments.length} comment${itemComments.length === 1 ? '' : 's'} on it.`
                      : 'Nobody commented on this item.'}
                  </p>
                </div>
                <button
                  className="review-btn review-btn-outline"
                  onClick={() => handleToggleChildReviewed(activeTab, false)}
                  disabled={markingReviewed}
                  style={{ fontSize: 13 }}
                >
                  Undo
                </button>
                <button
                  className="review-btn review-btn-primary"
                  onClick={() => setActiveTab('parent')}
                  style={{ fontSize: 13 }}
                >
                  Back to the decision
                </button>
              </div>
            ) : (
              <>
                <p style={{ fontSize: 13, color: '#555', margin: '0 0 10px' }}>
                  {itemComments.length > 0
                    ? `${itemComments.length} comment${itemComments.length === 1 ? '' : 's'} on this item.`
                    : 'Nobody commented on this item — minor and suggestion findings never block approval, so confirming is the only thing recording that you looked.'}
                </p>
                <button
                  className="review-btn review-btn-success"
                  onClick={() => handleToggleChildReviewed(activeTab, true)}
                  disabled={markingReviewed}
                  style={{ fontSize: 14, padding: '8px 18px' }}
                >
                  {markingReviewed ? 'Saving...' : "I've reviewed this item"}
                </button>
              </>
            )}
          </div>
        );
      })()}

      {/* Decision Section — parent tab only, and only while the cycle is
          still decidable OR still holds the DM's confirm-fixes act.
          Terminal cycles keep the read-only decision summary above. */}
      {activeTab === 'parent' && (!cycleIsTerminal || cycle.pendingFixes) && (
      <div className="review-section" style={{ background: '#f8f9fa', padding: 20, borderRadius: 12 }}>
        <h3>Final Decision</h3>
        <div className="review-form-group">
          {/* Spec §9 / Phase 4b — change spec: the applied findings as a
              per-format handoff (CMS change list, timestamped edit list,
              comment sheet fallback). Generated output only; it is also
              embedded in the revision email. */}
          {findingRoots.some(c => c.disposition === 'apply') && (
            <ChangeSpecPanel
              cycleId={cycleId}
              getAuthHeaders={getAuthHeaders}
              toApplyCount={findingRoots.filter(c => c.disposition === 'apply').length}
            />
          )}
          {!cycleIsTerminal && (
            <>
              <label>Decision Maker Notes</label>
              <textarea
                value={decisionNotes}
                onChange={e => setDecisionNotes(e.target.value)}
                placeholder="Notes on your decision, instructions for revision, etc."
                rows={3}
              />
            </>
          )}
        </div>
        {/* Q3 (2026-08-24) — Approve no longer always ships in the same turn.
            With findings dispositioned Apply, the cycle is approved and HELD:
            CellcoHub writes what it can into the working file, the producer
            finishes the rest and confirms, and only then does it distribute.
            Said here, at the moment of the click, because a decision maker who
            expects immediate publication would otherwise read the delay as a
            failure. */}
        {findingRoots.filter(c => c.disposition === 'apply').length > 0 && (
          <div
            data-testid="approve-holds-notice"
            style={{ fontSize: 13, color: '#1e4620', background: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: 6, padding: '10px 12px', margin: '0 0 12px' }}
          >
            <strong>Approving will not publish yet.</strong>{' '}
            {findingRoots.filter(c => c.disposition === 'apply').length} finding
            {findingRoots.filter(c => c.disposition === 'apply').length === 1 ? ' is' : 's are'} marked
            Apply, so this becomes <em>approved, pending fixes</em>: the fixes go into the working
            file, the producer confirms, and distribution happens then. No further review round.
          </div>
        )}
        {/* Rendered HERE, immediately above the decision bar, rather than up by
            the sequence strip: this is the moment the DM is about to act, and a
            disabled Approve with the explanation 400px away is the disabled
            button with no reason that this module keeps having to fix. */}
        {/* Approved-and-HELD is the one state where the DM's remaining act
            lives on this page and was not on it (2026-08-31, mid-walk: the
            first held cycle ever, and Confirm fixes was reachable only by
            switching to Reviewer view). The mount in ReviewInterface carries
            the comment "canConfirm decides who gets the button; the mount
            decides nobody" — this was that, for the decision maker. */}
        {cycle.pendingFixes && (
          <ConfirmFixesPanel
            cycleId={cycle._id}
            amendment={(cycle as any).amendment}
            canConfirm
            onConfirmed={fetchCycle}
            getAuthHeaders={getAuthHeaders}
          />
        )}
        <OpenStepPanel
          cycle={cycle}
          isDecisionMaker
          userEmail={user?.email}
          getAuthHeaders={getAuthHeaders}
          onCompleted={fetchCycle}
        />
        {!cycleIsTerminal && (
        <div className="review-action-bar">
          <button
            className="review-btn review-btn-success"
            onClick={() => handleDecision('approved')}
            disabled={submitting || stepsOpen.length > 0}
            title={stepsOpen.length > 0
              ? `Waiting on the ${stepsOpen.join(' and ')} step${stepsOpen.length === 1 ? '' : 's'}. Complete or stand in above, then approve.`
              : 'Approve. With findings marked Apply, the content is held until the fixes are confirmed.'}
          >
            Approve
          </button>
          <button
            className="review-btn review-btn-warning"
            onClick={() => handleDecision('revised')}
            disabled={submitting}
            title="Send it back for changes, then another review round"
          >
            Revision Needed
          </button>
          <button
            className="review-btn review-btn-danger"
            onClick={() => handleDecision('rejected')}
            disabled={submitting}
          >
            Reject
          </button>
        </div>
        )}
        {error && (
          <div id="decision-error" role="alert" style={{ color: '#842029', padding: '10px 12px', background: '#f8d7da', border: '1px solid #f1aeb5', borderRadius: 8, marginTop: 10, fontSize: 13 }}>
            {error}
          </div>
        )}
      </div>
      )}
      {reasonTarget && (
        <div role="dialog" aria-modal="true" aria-label="Reason for won't apply"
             style={{ position: 'fixed', inset: 0, background: 'rgba(18,50,80,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
             onClick={() => setReasonTarget(null)}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 20, width: 'min(520px, 92vw)', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 6px', fontSize: 16, color: '#123250' }}>Won't apply — why?</h3>
            <p style={{ margin: '0 0 10px', fontSize: 13, color: '#555' }}>
              <span className={`severity-badge ${reasonTarget.severity}`} style={{ marginRight: 6 }}>{reasonTarget.severity}</span>
              {reasonTarget.issue}
            </p>
            <textarea
              autoFocus
              value={reasonText}
              onChange={e => setReasonText(e.target.value)}
              rows={3}
              placeholder="e.g. Claim is already qualified on slide 2; legal signed off 2026-08-12"
              style={{ width: '100%', fontSize: 14, padding: 8, border: `1px solid ${reasonError ? '#dc3545' : '#ced4da'}`, borderRadius: 6, boxSizing: 'border-box' }}
            />
            {reasonError && <p style={{ color: '#dc3545', fontSize: 12, margin: '6px 0 0' }}>{reasonError}</p>}
            <p style={{ fontSize: 12, color: '#6c757d', margin: '8px 0 12px' }}>This reason is stored with the finding and shown to reviewers and the producer.</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="review-btn review-btn-outline" onClick={() => setReasonTarget(null)}>Cancel</button>
              <button className="review-btn review-btn-warning" onClick={saveWontApplyReason}>Save reason</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Reviewers Modal */}
      {showEditReviewers && (
        <div className="review-modal-overlay" onClick={() => setShowEditReviewers(false)}>
          <div className="review-modal" onClick={e => e.stopPropagation()}>
            <h3>Edit Reviewers</h3>
            <div className="review-form-group">
              <label style={{ marginBottom: 8, display: 'block' }}>Select Reviewers</label>
              <div className="reviewer-picker reviewer-picker-modal">
                {teamMembers.length === 0 && (
                  <p style={{ fontSize: 13, color: '#999', margin: 0 }}>No team members available.</p>
                )}
                {teamMembers.map(m => {
                  const isSelected = selectedReviewerIds.includes(m._id);
                  return (
                    <button
                      key={m._id}
                      type="button"
                      className={`reviewer-chip ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleReviewer(m._id)}
                    >
                      <span className="reviewer-chip-avatar">{m.name.charAt(0)}</span>
                      <span>{m.name}</span>
                      {isSelected && <span className="reviewer-chip-check">&#10003;</span>}
                    </button>
                  );
                })}
              </div>
              {selectedReviewerIds.length > 0 && (
                <p style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                  {selectedReviewerIds.length} reviewer{selectedReviewerIds.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>
            <div className="review-action-bar">
              <button
                className="review-btn review-btn-primary"
                onClick={saveReviewers}
                disabled={savingReviewers || selectedReviewerIds.length === 0}
              >
                {savingReviewers ? 'Saving...' : 'Save Reviewers'}
              </button>
              <button className="review-btn review-btn-outline" onClick={() => setShowEditReviewers(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Comment card with text selection + timestamp display
const DecisionCommentCard: React.FC<{
  comment: ReviewComment;
  onToggleResolved: (c: ReviewComment) => void;
  onSetDisposition?: (c: ReviewComment, d: Disposition) => void;
  /** Inside a rail row: the row owns the id anchor and already shows the
      severity and resolved state. See CommentCard.inRail — a duplicated
      `comment-<id>` breaks every marker click, silently. */
  inRail?: boolean;
  /**
   * May this viewer edit THIS comment? (2026-08-26, Fredrik: "only the
   * creator, DM and Admin should be able to edit someone else's comment.")
   *
   * PUT /comments/:id has allowed the author or a decision maker since it
   * shipped, but this card had no Edit affordance at all — so the DM, the one
   * person the rule exists FOR, could not use it, while the reviewer card was
   * handing the button to everyone. The permission was right in the backend
   * and wrong at both ends of the UI, in opposite directions.
   */
  canEdit?: boolean;
  /** Delete is AUTHOR-only (Fredrik, 2026-09-02) — narrower than canEdit,
      which the DM holds for everyone's comments. */
  canDelete?: boolean;
  getAuthHeaders?: () => Promise<Record<string, string>>;
  onEdited?: () => void;
}> = ({ comment: c, onToggleResolved, onSetDisposition, inRail = false,
        canEdit = false, canDelete = false, getAuthHeaders, onEdited }) => {
  const [editing, setEditing] = useState(false);
  return (
  <div
    id={inRail ? undefined : `comment-${c._id}`}
    className={`comment-item ${c.severity} ${c.resolved ? 'resolved' : ''}${inRail ? ' in-rail' : ''}`}
  >
    <div className="comment-header">
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {!inRail && <span className={`severity-badge ${c.severity}`}>{c.severity}</span>}
        {c.timestamp !== undefined && (
          <span style={{ fontSize: 11, color: '#666', background: '#f0f0f0', padding: '1px 8px', borderRadius: 4 }}>
            at {Math.floor(c.timestamp / 60)}:{String(c.timestamp % 60).padStart(2, '0')}
          </span>
        )}
        {c.stateLabel && c.stateId && c.stateId !== 'default' && (
          <span style={{ fontSize: 11, color: '#123250', background: '#e8f0fa', padding: '1px 8px', borderRadius: 4, fontWeight: 500 }}>
            {c.stateLabel}
          </span>
        )}
        {c.resolved && !inRail && <span style={{ color: '#28a745', fontSize: 12, fontWeight: 600 }}>Resolved</span>}
        {/* Spec §2.2 — disposition control. Findings (roots) only; the DM
            decides, everyone sees the decision. Absent === undecided. */}
        {carriesDisposition(c) && (
          onSetDisposition ? (
            <select
              aria-label="Disposition"
              value={c.disposition || 'undecided'}
              onChange={e => onSetDisposition(c, e.target.value as Disposition)}
              style={{
                fontSize: 12, padding: '2px 6px', borderRadius: 4,
                border: `1px solid ${isUndecided(c) ? ((c.severity === 'critical' || c.severity === 'major') ? '#dc3545' : '#ced4da') : '#28a745'}`,
                color: isUndecided(c) ? '#495057' : '#155724', background: isUndecided(c) ? '#fff' : '#eaf7ee'
              }}
              title={c.dispositionBy?.name ? `${DISPOSITION_LABELS[c.disposition || 'undecided']} — ${c.dispositionBy.name}` : 'Decide what happens to this finding'}
            >
              {(Object.keys(DISPOSITION_LABELS) as Disposition[]).map(d => (
                <option key={d} value={d}>{DISPOSITION_LABELS[d]}</option>
              ))}
            </select>
          ) : (
            <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 4, background: isUndecided(c) ? '#f0f0f0' : '#eaf7ee', color: isUndecided(c) ? '#666' : '#155724', fontWeight: 600 }}>
              {DISPOSITION_LABELS[c.disposition || 'undecided']}
            </span>
          )
        )}
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        {canEdit && !editing && getAuthHeaders && (
          <button
            data-testid="comment-edit"
            onClick={() => setEditing(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#123250', fontSize: 12, fontWeight: 600 }}
          >
            Edit
          </button>
        )}
        <button
          onClick={() => onToggleResolved(c)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.resolved ? '#dc3545' : '#28a745', fontSize: 12, fontWeight: 600 }}
        >
          {c.resolved ? 'Reopen' : 'Resolve'}
        </button>
      </div>
    </div>
    {c.textSelection ? (
      <div style={{ fontSize: 12, color: '#555', fontStyle: 'italic', background: '#f0f7ff', padding: '4px 10px', borderLeft: '3px solid #123250', borderRadius: '0 4px 4px 0', margin: '4px 0 6px', maxHeight: 60, overflowY: 'auto' }}>
        "{c.textSelection}"
      </div>
    ) : c.sectionContent ? (
      <div style={{ fontSize: 12, color: '#555', fontStyle: 'italic', background: '#f5f5f5', padding: '4px 10px', borderLeft: '3px solid #999', borderRadius: '0 4px 4px 0', margin: '4px 0 6px', maxHeight: 60, overflowY: 'auto' }}>
        {c.sectionContent}
      </div>
    ) : null}
    {/* WS1.7 — @tokens as pills (solid = resolved, dashed = probable typo). */}
    {editing && getAuthHeaders ? (
      <CommentEditForm
        comment={c}
        getAuthHeaders={getAuthHeaders}
        onSaved={onEdited}
        onCancel={() => setEditing(false)}
        canDelete={canDelete}
      />
    ) : (
      <p style={{ margin: '4px 0', fontSize: 14 }}>{renderWithMentions(c.issue, c.mentionedTeamMembers)}</p>
    )}
    {c.location && <p style={{ margin: '2px 0', fontSize: 12, color: '#666' }}>Location: {c.location}</p>}
    {c.suggestedFix && (
      <p style={{ margin: '4px 0', fontSize: 13, color: '#155724', background: '#d4edda', padding: '6px 10px', borderRadius: 6 }}>
        Suggested fix: {c.suggestedFix}
      </p>
    )}
    {c.disposition === 'wont_apply' && c.dispositionReason && (
      <p style={{ margin: '4px 0', fontSize: 12, color: '#664d03', background: '#fff3cd', padding: '4px 10px', borderRadius: 6 }}>
        Won't apply — {c.dispositionReason}{c.dispositionBy?.name ? ` (${c.dispositionBy.name})` : ''}
      </p>
    )}
  </div>
  );
};

export default DecisionView;
